import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { Input } from "@/shared/components/Input";
import { CampoDataNascimento } from "@/shared/components/CampoDataNascimento";
import { CampoSexo } from "@/shared/components/CampoSexo";
import { Switch } from "@/shared/components/Switch";
import type { ContatoIgreja } from "@/core/utils/contatoIgreja";
import { AvisoConsentimento } from "@/modules/visitantes/components/AvisoConsentimento";
import { CampoSelect, CampoTexto } from "@/modules/visitantes/components/Campos";
import { SobreAPessoa } from "@/modules/visitantes/components/SobreAPessoa";
import type {
  DadosVisitante,
  MinisterioLider,
} from "@/modules/visitantes/services/visitantesService";
import {
  IDADE_CRIANCA_ATE,
  calcularIdade,
  hojeSaoPaulo,
  ESTADOS_CIVIS,
  mascararTelefoneDigitando,
  normalizarNome,
  soDigitos,
  telefoneValido,
} from "@/modules/visitantes/utils/regras";

export type ModoFormulario = "cadastro" | "edicao" | "informe" | "avaliacao";

export const DADOS_VAZIOS: DadosVisitante = {
  nome: "",
  telefone: "",
  email: "",
  estadoCivil: "",
  sexo: "",
  nascimento: "",
  dataVisita: "",
  respNome: "",
  respTelefone: "",
  igrejaSituacao: "",
  igrejaNome: "",
  cidade: "",
  bairro: "",
  endereco: "",
  preferenciaContato: "",
  pedidoOracao: "",
  observacao: "",
  ministerioId: "",
};

interface Props {
  modo: ModoFormulario;
  inicial?: Partial<DadosVisitante>;
  /** Ministérios que o líder lidera (só no informe). */
  ministerios?: MinisterioLider[];
  /** Visitantes já cadastrados que a pessoa enxerga — só para avisar de nome repetido. */
  existentes?: { id: string; nome: string }[];
  /** Id do próprio visitante na edição (não conta como repetido). */
  idAtual?: string;
  enviando: boolean;
  /** Erro vindo do banco na última tentativa. */
  erro?: string | null;
  /** Contato da igreja que aparece no aviso de privacidade (cadastro). */
  contatoIgreja?: Partial<ContatoIgreja> | null;
  /**
   * Edição de quem retirou o consentimento: "Sobre a pessoa" não é guardado
   * (só "prefere não ser procurado(a)"), igual à regra do banco.
   */
  semConsentimento?: boolean;
  /** No cadastro e no Visitante pendente: habilita "Veio com a família?" e leva à etapa 2 em vez de enviar. */
  onFamilia?: (dados: DadosVisitante) => void;
  /** Edição do Visitante pendente: o consentimento já foi registrado na criação (não pergunta de novo). */
  consentimentoRegistrado?: boolean;
  /** Ao voltar da etapa 2, o interruptor volta ligado. */
  familiaInicial?: boolean;
  /** `consentimento` = "avisei a pessoa e ela concordou" (só tem sentido no cadastro). */
  onEnviar: (dados: DadosVisitante, consentimento: boolean) => void;
  onCancelar: () => void;
}

const TEXTO_BOTAO: Record<ModoFormulario, string> = {
  cadastro: "Cadastrar",
  edicao: "Salvar",
  informe: "Cadastrar",
  avaliacao: "Adicionar",
};

type Erros = Partial<Record<keyof DadosVisitante | "responsavel", string>>;

/**
 * Formulário único do elo (cadastro, edição, informe do líder e avaliação do informado).
 * A validação aqui é só conveniência (mesmas regras de `_visitante_validar`);
 * o banco revalida tudo e devolve a mensagem em português.
 */
export function FormularioVisitante({
  modo,
  inicial,
  ministerios = [],
  existentes = [],
  idAtual,
  enviando,
  erro,
  contatoIgreja,
  semConsentimento = false,
  onFamilia,
  consentimentoRegistrado = false,
  familiaInicial = false,
  onEnviar,
  onCancelar,
}: Props) {
  const hoje = hojeSaoPaulo();
  const [d, setD] = useState<DadosVisitante>({
    ...DADOS_VAZIOS,
    dataVisita: modo === "edicao" ? "" : hoje,
    ...inicial,
  });
  const [erros, setErros] = useState<Erros>({});
  const [consentimento, setConsentimento] = useState(false);
  const [comFamilia, setComFamilia] = useState(familiaInicial);
  const [erroConsentimento, setErroConsentimento] = useState<string | undefined>();

  const mudar = <K extends keyof DadosVisitante>(campo: K, valor: DadosVisitante[K]) =>
    setD((atual) => ({ ...atual, [campo]: valor }));

  const idade = calcularIdade(d.nascimento, hoje);
  const ehCrianca = idade !== null && idade <= IDADE_CRIANCA_ATE;
  const ehAdolescente = idade !== null && idade > IDADE_CRIANCA_ATE && idade < 18;
  const pedeResponsavel = ehCrianca || ehAdolescente;
  // Cadastro completo = o do ministério responsável e o do Visitante pendente (líder de outro ministério).
  const cadastroCompleto = modo === "cadastro" || modo === "informe";
  // Sexo é obrigatório só em cadastro novo (ministério responsável e informe do líder). Na edição,
  // na avaliação e na edição do pendente fica opcional: cadastros antigos não têm o campo.
  const sexoObrigatorio = cadastroCompleto && !consentimentoRegistrado;
  // Cadastro em família parte de um adulto (o banco exige 18+); sem data ainda, deixa a opção visível.
  const podeFamilia = cadastroCompleto && !!onFamilia && (idade === null || idade >= 18);
  const familiaAtiva = podeFamilia && comFamilia;
  const pedeConsentimento = cadastroCompleto && !consentimentoRegistrado && !familiaAtiva;
  // Criança não guarda "Sobre a pessoa" (regra do banco, VIS-DEC-034).
  const mostraSobre = !ehCrianca;
  const querVisita = d.preferenciaContato === "visita";
  const mostraDataVisita = modo !== "edicao";
  const rotuloObs =
    modo === "informe" ? "Recado para o ministério (opcional)" : "Observação (opcional)";

  const repetidos = useMemo(() => {
    const alvo = normalizarNome(d.nome);
    if (alvo.length < 3) return [];
    return existentes.filter((e) => e.id !== idAtual && normalizarNome(e.nome) === alvo);
  }, [d.nome, existentes, idAtual]);

  function validar(): Erros {
    const e: Erros = {};
    const nome = d.nome.trim();
    if (nome.length < 3 || nome.length > 100) e.nome = "Informe o nome (de 3 a 100 caracteres).";
    if (!d.nascimento) e.nascimento = "Informe a data de nascimento.";
    else if (d.nascimento > hoje || d.nascimento < "1900-01-01")
      e.nascimento = "Data de nascimento inválida.";
    if (sexoObrigatorio && !d.sexo) e.sexo = "Informe o sexo.";
    if (d.telefone && !telefoneValido(d.telefone))
      e.telefone = "Telefone inválido. Use DDD e número.";
    if (d.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(d.email.trim())) e.email = "E-mail inválido.";
    if (mostraDataVisita) {
      if (!d.dataVisita) e.dataVisita = "Informe a data da visita.";
      else if (d.dataVisita > hoje || (d.nascimento && d.dataVisita < d.nascimento))
        e.dataVisita = "Data da visita inválida.";
    }
    // Menor de 18 anos: o contato do responsável é obrigatório (VIS-DEC-038).
    if (pedeResponsavel && !d.respNome.trim()) e.respNome = "Informe o nome do responsável.";
    else if (pedeResponsavel && d.respNome.trim().length < 3)
      e.respNome = "Nome do responsável inválido.";
    if (pedeResponsavel && !d.respTelefone) e.respTelefone = "Informe o telefone do responsável.";
    else if (pedeResponsavel && !telefoneValido(d.respTelefone))
      e.respTelefone = "Telefone do responsável inválido. Use DDD e número.";
    if (querVisita) {
      if (!d.endereco.trim()) e.endereco = "Informe rua e número.";
      if (!d.bairro.trim()) e.bairro = "Informe o bairro.";
      if (!d.cidade.trim()) e.cidade = "Informe a cidade.";
    }
    return e;
  }

  function enviar(ev: FormEvent) {
    ev.preventDefault();
    const e = validar();
    setErros(e);
    if (pedeConsentimento && !consentimento) {
      setErroConsentimento(
        ehCrianca
          ? "Confirme que o responsável foi avisado e concordou."
          : "Confirme que a pessoa foi avisada e concordou.",
      );
      return;
    }
    setErroConsentimento(undefined);
    if (Object.keys(e).length > 0) return;
    const limpo: DadosVisitante = {
      ...d,
      nome: d.nome.trim(),
      telefone: soDigitos(d.telefone),
      respTelefone: pedeResponsavel ? soDigitos(d.respTelefone) : "",
      // Estado civil só de quem tem 12 anos ou mais.
      estadoCivil: ehCrianca ? "" : d.estadoCivil,
      // Responsável só vale para quem tem menos de 18 anos.
      respNome: pedeResponsavel ? d.respNome.trim() : "",
      igrejaNome: d.igrejaSituacao === "outra" ? d.igrejaNome.trim() : "",
      bairro: querVisita ? d.bairro.trim() : "",
      endereco: querVisita ? d.endereco.trim() : "",
    };
    if (ehCrianca) {
      // Criança: nenhuma resposta de "Sobre a pessoa".
      Object.assign(limpo, {
        igrejaSituacao: "",
        igrejaNome: "",
        cidade: "",
        bairro: "",
        endereco: "",
        preferenciaContato: "",
        pedidoOracao: "",
      });
    }
    if (semConsentimento) {
      // Consentimento retirado: só a preferência "prefere não ser procurado(a)" continua.
      Object.assign(limpo, {
        igrejaSituacao: "",
        igrejaNome: "",
        cidade: "",
        bairro: "",
        endereco: "",
        preferenciaContato: d.preferenciaContato === "nao" ? "nao" : "",
        pedidoOracao: "",
      });
    }
    if (familiaAtiva && onFamilia) {
      onFamilia(limpo);
      return;
    }
    onEnviar(limpo, consentimento);
  }

  return (
    <form onSubmit={enviar} noValidate className="flex flex-col gap-4">
      <Input
        label="Nome completo *"
        name="nome"
        value={d.nome}
        maxLength={100}
        onChange={(e) => mudar("nome", e.target.value)}
        erro={erros.nome}
        autoComplete="off"
      />

      {repetidos.length > 0 && (
        <p role="status" className="rounded-md bg-warning/10 px-4 py-3 text-sm text-warning">
          Já existe alguém com este nome:{" "}
          {repetidos.map((r, i) => (
            <span key={r.id}>
              {i > 0 && ", "}
              <Link to={`/visitantes/${r.id}`} className="font-semibold underline">
                ver ficha de {r.nome}
              </Link>
            </span>
          ))}
          . Se for a mesma pessoa, registre uma nova visita na ficha em vez de cadastrar de novo.
        </p>
      )}

      <CampoDataNascimento
        label="Data de nascimento *"
        name="nascimento"
        max={hoje}
        value={d.nascimento}
        onChange={(iso) => mudar("nascimento", iso)}
        erro={erros.nascimento}
      />

      <CampoSexo
        legenda={sexoObrigatorio ? "Sexo *" : "Sexo"}
        valor={d.sexo}
        onChange={(v) => mudar("sexo", v)}
        erro={erros.sexo}
      />

      <Input
        label="Telefone"
        name="telefone"
        type="tel"
        inputMode="numeric"
        placeholder="(11) 91234-5678"
        value={mascararTelefoneDigitando(d.telefone)}
        onChange={(e) => mudar("telefone", soDigitos(e.target.value))}
        erro={erros.telefone}
      />

      {!ehCrianca && (
        <CampoSelect
          label="Estado civil"
          name="estadoCivil"
          value={d.estadoCivil}
          onChange={(e) => mudar("estadoCivil", e.target.value)}
        >
          <option value="">Não informar</option>
          {ESTADOS_CIVIS.map((o) => (
            <option key={o.chave} value={o.chave}>
              {o.rotulo}
            </option>
          ))}
        </CampoSelect>
      )}

      <Input
        label="E-mail"
        name="email"
        type="email"
        maxLength={100}
        value={d.email}
        onChange={(e) => mudar("email", e.target.value)}
        erro={erros.email}
      />

      {mostraDataVisita && (
        <Input
          label="Data da visita *"
          name="dataVisita"
          type="date"
          max={hoje}
          value={d.dataVisita ?? ""}
          onChange={(e) => mudar("dataVisita", e.target.value)}
          erro={erros.dataVisita}
        />
      )}

      {pedeResponsavel && (
        <fieldset className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-semibold text-text">Responsável *</legend>
          <p className="text-xs text-muted">
            {ehCrianca
              ? "Para criança, o contato é feito pelo responsável e só o ministério responsável vê o cadastro. "
              : "Menor de 18 anos: "}
            Nome e telefone do responsável são obrigatórios.
          </p>
          <Input
            label="Nome do responsável *"
            name="respNome"
            maxLength={100}
            value={d.respNome}
            onChange={(e) => mudar("respNome", e.target.value)}
            erro={erros.respNome}
          />
          <Input
            label="Telefone do responsável *"
            name="respTelefone"
            type="tel"
            inputMode="numeric"
            placeholder="(11) 91234-5678"
            value={mascararTelefoneDigitando(d.respTelefone)}
            onChange={(e) => mudar("respTelefone", soDigitos(e.target.value))}
            erro={erros.respTelefone}
          />
        </fieldset>
      )}

      {modo === "informe" && (
        <CampoSelect
          label="De qual ministério vem esta informação? (opcional)"
          name="ministerioId"
          value={d.ministerioId ?? ""}
          onChange={(e) => mudar("ministerioId", e.target.value)}
        >
          <option value="">Nenhum em especial</option>
          {ministerios.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nome}
            </option>
          ))}
        </CampoSelect>
      )}

      {semConsentimento && !ehCrianca && (
        <fieldset className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <legend className="px-1 text-sm font-semibold text-text">Sobre a pessoa</legend>
          <p className="flex items-start gap-1.5 text-xs text-muted">
            <Lock size={14} className="mt-px shrink-0" aria-hidden="true" />A pessoa retirou o
            consentimento: as respostas de “Sobre a pessoa” não são guardadas. Para voltar a
            guardar, registre o consentimento de novo na ficha.
          </p>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-text">
            <input
              type="checkbox"
              checked={d.preferenciaContato === "nao"}
              onChange={(e) => mudar("preferenciaContato", e.target.checked ? "nao" : "")}
              className="h-5 w-5 shrink-0 accent-primary"
            />
            Prefere não ser procurado(a)
          </label>
        </fieldset>
      )}

      {!semConsentimento && mostraSobre && (
        <SobreAPessoa
          valor={d}
          onMudar={(parcial) => setD((atual) => ({ ...atual, ...parcial }))}
          erros={erros}
        />
      )}

      <CampoTexto
        label={rotuloObs}
        name="observacao"
        maxLength={500}
        value={d.observacao}
        onChange={(e) => mudar("observacao", e.target.value)}
      />

      {podeFamilia && (
        <div className="flex flex-col gap-1 rounded-lg border border-border p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold text-text">Veio com a família?</span>
            <Switch checked={comFamilia} onChange={setComFamilia} label="Veio com a família?" />
          </div>
          <p className="text-xs text-muted">
            Cônjuge, filhos ou outros: cadastre todos juntos na próxima etapa.
          </p>
        </div>
      )}

      {pedeConsentimento && (
        <AvisoConsentimento
          variante={ehCrianca ? "responsavel" : "pessoa"}
          contato={contatoIgreja}
          marcado={consentimento}
          onMarcar={(v) => {
            setConsentimento(v);
            if (v) setErroConsentimento(undefined);
          }}
          erro={erroConsentimento}
          adolescente={ehAdolescente}
        />
      )}
      {familiaAtiva && (
        <p className="text-xs text-muted">O aviso e a confirmação ficam no fim da etapa 2.</p>
      )}

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
          {erro}
        </p>
      )}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <BotaoVoltar onClick={onCancelar} disabled={enviando} className="min-h-11" />
        <Button
          type="submit"
          carregando={enviando}
          disabled={pedeConsentimento && !consentimento}
          className="min-h-11"
        >
          {familiaAtiva ? "Continuar" : TEXTO_BOTAO[modo]}
        </Button>
      </div>
    </form>
  );
}
